import * as React from "react";
import { useState, useEffect } from "react";
import { LocalizedLink } from "@/components/LocalizedLink";
import Navigation from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowUp, Check, X, Sparkles, Zap, Droplets, Wind, Shield, Thermometer, Heart, Star, Cpu, Sun } from "lucide-react";
import { SEO } from "@/components/SEO";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import { useOptimizedObserver } from "@/hooks/useOptimizedObserver";
import { Footer } from "@/components/Footer";
import { ProminentCTA } from "@/components/ui/prominent-cta";

// Product Images
import l1ProductImage from "@/assets/l1-product-main.png";
import m3ProductImage from "@/assets/m3-hero-main.jpg";
import lunaProductImage from "@/assets/luna-series-product-main.jpg";

export default function L1vsM3vsLuna() {
  const [showBackToTop, setShowBackToTop] = useState(false);
  useScrollToTop();
  useOptimizedObserver();

  useEffect(() => {
    let rafId: number | null = null;
    
    const handleScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        setShowBackToTop(window.scrollY > 400);
        rafId = null;
      });
    };
    
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const comparisonSpecs = [
    { label: "Design Type", l1: "Compact One-Piece", m3: "Elongated One-Piece", luna: "Classic Wellness", highlight: true },
    { label: "Primary Focus", l1: "26 Premium Features", m3: "Voice Control Tech", luna: "Health & Wellness", highlight: true },
    { label: "Dimensions (L×W×H)", l1: "650mm × 380mm × 520mm", m3: "680mm × 380mm × 520mm", luna: "660mm × 385mm × 780mm", highlight: false },
    { label: "Product Weight", l1: "48kg", m3: "55kg", luna: "62kg", highlight: false },
    { label: "Flush System", l1: "Super spin siphonic", m3: "Super spin siphonic", luna: "Dual flush system", highlight: false },
    { label: "Flush Volume", l1: "4L / 6L dual flush", m3: "4L / 6L dual flush", luna: "4.5L / 3L dual flush", highlight: false },
    { label: "Water Heating", l1: "Instant tankless", m3: "Instant tankless", luna: "Therapeutic warm water", highlight: false },
    { label: "Seat Heating", l1: "4-level control", m3: "Thermostatic 36°C", luna: "Temperature controlled", highlight: false },
    { label: "Air Dryer", l1: "Warm air system", m3: "Multi-level warm air", luna: "Gentle warm air", highlight: false },
    { label: "UV Sterilization", l1: "✓ UV LED system", m3: "—", luna: "—", highlight: true },
    { label: "Foam Shield", l1: "✓", m3: "—", luna: "—", highlight: true },
    { label: "Voice Control", l1: "—", m3: "✓", luna: "—", highlight: true },
    { label: "Radar Sensor", l1: "—", m3: "✓", luna: "—", highlight: true },
    { label: "Auto Open/Close", l1: "✓", m3: "✓ Radar sensor", luna: "✓", highlight: false },
    { label: "Auto Flush", l1: "✓", m3: "✓", luna: "✓", highlight: false },
    { label: "Foot Kick Sensor", l1: "—", m3: "✓", luna: "—", highlight: false },
    { label: "LED Night Light", l1: "✓ Ambient", m3: "✓", luna: "✓", highlight: false },
    { label: "Wash Modes", l1: "Female, Rear, Massage", m3: "Female, Rear, Massage", luna: "Therapeutic modes", highlight: false },
    { label: "Aromatherapy", l1: "—", m3: "—", luna: "✓", highlight: true },
    { label: "Health Sensors", l1: "—", m3: "—", luna: "✓", highlight: true },
    { label: "Deodorization", l1: "✓", m3: "✓ Auto", luna: "✓ Air purification", highlight: false },
    { label: "Self-Cleaning Nozzle", l1: "✓", m3: "✓", luna: "✓", highlight: false },
    { label: "Power Consumption", l1: "1200W (max)", m3: "1300W (max)", luna: "1200W (max)", highlight: false },
    { label: "Warranty", l1: "3 Years", m3: "3 Years", luna: "5 Years", highlight: false },
    { label: "Price Range", l1: "Premium", m3: "Premium", luna: "Premium", highlight: false }
  ];

  const l1Highlights = [
    { icon: <Cpu className="w-6 h-6" />, text: "26 premium features in compact design" },
    { icon: <Shield className="w-6 h-6" />, text: "UV LED sterilization system" },
    { icon: <Droplets className="w-6 h-6" />, text: "Foam shield splash prevention" },
    { icon: <Sun className="w-6 h-6" />, text: "Ambient LED night lighting" }
  ];

  const m3Highlights = [
    { icon: <Zap className="w-6 h-6" />, text: "Voice control for hands-free operation" },
    { icon: <Sparkles className="w-6 h-6" />, text: "Radar sensor auto detection" },
    { icon: <Wind className="w-6 h-6" />, text: "Foot kick seat ring control" },
    { icon: <Thermometer className="w-6 h-6" />, text: "Thermostatic seat at 36°C" }
  ];

  const lunaHighlights = [
    { icon: <Heart className="w-6 h-6" />, text: "Wellness-focused therapeutic features" },
    { icon: <Sparkles className="w-6 h-6" />, text: "Built-in aromatherapy system" },
    { icon: <Star className="w-6 h-6" />, text: "Health monitoring sensors" },
    { icon: <Wind className="w-6 h-6" />, text: "Air purification technology" }
  ];

  const productStructuredData = {
    "@context": "https://schema.org",
    "@type": "ComparisonPage",
    "name": "L1 vs M3 vs Luna Smart Toilet Comparison",
    "description": "Compare the ZENO L1 compact design, M3 Voice Control, and Luna wellness-focused smart toilets. Complete feature-by-feature comparison guide.",
    "mainEntity": [
      {
        "@type": "Product",
        "name": "ZENO L1 Smart Bidet Toilet",
        "description": "Compact smart toilet with 26 premium features including UV sterilization"
      },
      {
        "@type": "Product",
        "name": "M3 Smart Toilet",
        "description": "Voice control and radar sensor smart toilet with advanced technology"
      },
      {
        "@type": "Product",
        "name": "Luna Smart Toilet",
        "description": "Wellness-focused smart toilet with therapeutic features and health monitoring"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-stone-50 text-foreground">
      <SEO
        title="L1 vs M3 vs LUNA | Compact Smart Toilets Compared"
        description="Compare ZENO L1 compact design, M3 Voice Control, and Luna wellness smart toilets. Feature-by-feature comparison of technology, design, and specifications."
        keywords="L1 vs M3 vs Luna, compact smart toilet, voice control toilet, wellness toilet, smart toilet comparison 2025"
        url="/compare/l1-vs-m3-vs-luna"
        structuredData={productStructuredData}
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Smart Toilets", url: "/smart-toilets" },
          { name: "L1 vs M3 vs Luna Comparison", url: "/compare/l1-vs-m3-vs-luna" }
        ]}
      />
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-28 pb-16 px-6 lg:px-12">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-12 fade-in-observer">
            <Badge variant="outline" className="border-primary/50 text-primary bg-primary/5 mb-4">
              Compact Model Comparison
            </Badge>
            <h1 className="font-playfair text-4xl md:text-6xl font-light text-primary mb-4">
              L1 <span className="text-muted-foreground">vs</span> M3 <span className="text-muted-foreground">vs</span> Luna
            </h1>
            <p className="font-inter text-xl text-muted-foreground max-w-3xl mx-auto">
              Compare compact smart toilets with unique strengths. UV sterilization, voice control, or wellness features.
            </p>
          </div>

          {/* Product Cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-16 fade-in-observer">
            {/* L1 Card */}
            <Card className="overflow-hidden shadow-luxury border-cyan-200/50 hover:shadow-gold transition-all duration-500">
              <div className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-stone-100 to-stone-50">
                <img loading="lazy" decoding="async" 
                  src={l1ProductImage}
                  alt="ZENO L1 compact smart bidet toilet with UV sterilization"
                  className="w-full h-full object-contain p-4"
                />
                <Badge className="absolute top-4 left-4 bg-cyan-600 text-white">
                  26 Features
                </Badge>
              </div>
              <CardContent className="p-6">
                <h2 className="font-playfair text-2xl font-bold text-primary mb-2">ZENO L1</h2>
                <p className="text-muted-foreground mb-4 text-sm">Compact design with UV sterilization</p>
                <div className="space-y-2">
                  {l1Highlights.map((item, index) => (
                    <div key={index} className="flex items-center gap-2 text-xs">
                      <span className="text-cyan-500">{item.icon}</span>
                      <span className="text-muted-foreground">{item.text}</span>
                    </div>
                  ))}
                </div>
                <Button asChild className="w-full mt-4 bg-cyan-600 hover:bg-cyan-700">
                  <LocalizedLink to="/products/l1">View L1 Details</LocalizedLink>
                </Button>
              </CardContent>
            </Card>

            {/* M3 Card */}
            <Card className="overflow-hidden shadow-luxury border-blue-200/50 hover:shadow-gold transition-all duration-500">
              <div className="relative aspect-[4/3] overflow-hidden">
                <img loading="lazy" decoding="async" 
                  src={m3ProductImage}
                  alt="M3 smart toilet with voice control and radar sensor technology"
                  className="w-full h-full object-cover"
                />
                <Badge className="absolute top-4 left-4 bg-blue-600 text-white">
                  Voice Control
                </Badge>
              </div>
              <CardContent className="p-6">
                <h2 className="font-playfair text-2xl font-bold text-primary mb-2">M3 SMART</h2>
                <p className="text-muted-foreground mb-4 text-sm">Voice control & radar sensor technology</p>
                <div className="space-y-2">
                  {m3Highlights.map((item, index) => (
                    <div key={index} className="flex items-center gap-2 text-xs">
                      <span className="text-blue-500">{item.icon}</span>
                      <span className="text-muted-foreground">{item.text}</span>
                    </div>
                  ))}
                </div>
                <Button asChild className="w-full mt-4 bg-blue-600 hover:bg-blue-700">
                  <LocalizedLink to="/products/m3">View M3 Details</LocalizedLink>
                </Button>
              </CardContent>
            </Card>

            {/* Luna Card */}
            <Card className="overflow-hidden shadow-luxury border-purple-200/50 hover:shadow-gold transition-all duration-500">
              <div className="relative aspect-[4/3] overflow-hidden">
                <img loading="lazy" decoding="async" 
                  src={lunaProductImage}
                  alt="Luna wellness smart toilet with therapeutic features"
                  className="w-full h-full object-cover"
                />
                <Badge className="absolute top-4 left-4 bg-purple-600 text-white">
                  Wellness
                </Badge>
              </div>
              <CardContent className="p-6">
                <h2 className="font-playfair text-2xl font-bold text-primary mb-2">LUNA</h2>
                <p className="text-muted-foreground mb-4 text-sm">Wellness-focused therapeutic features</p>
                <div className="space-y-2">
                  {lunaHighlights.map((item, index) => (
                    <div key={index} className="flex items-center gap-2 text-xs">
                      <span className="text-purple-500">{item.icon}</span>
                      <span className="text-muted-foreground">{item.text}</span>
                    </div>
                  ))}
                </div>
                <Button asChild className="w-full mt-4 bg-purple-600 hover:bg-purple-700">
                  <LocalizedLink to="/products/luna">View Luna Details</LocalizedLink>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-16 px-6 lg:px-12 bg-white/80 backdrop-blur-sm fade-in-observer">
        <div className="container mx-auto max-w-6xl">
          <h2 className="font-playfair text-3xl font-medium text-primary mb-8 text-center">Feature Comparison</h2>
          
          <div className="bg-white rounded-2xl shadow-luxury overflow-hidden overflow-x-auto">
            {/* Table Header */}
            <div className="grid grid-cols-4 bg-gradient-to-r from-primary to-primary/90 text-primary-foreground min-w-[600px]">
              <div className="p-4 font-medium text-sm">Feature</div>
              <div className="p-4 font-medium text-sm text-center border-l border-white/20">
                <span className="flex items-center justify-center gap-2">
                  <Cpu className="w-4 h-4" /> ZENO L1
                </span>
              </div>
              <div className="p-4 font-medium text-sm text-center border-l border-white/20">
                <span className="flex items-center justify-center gap-2">
                  <Zap className="w-4 h-4" /> M3 Smart
                </span>
              </div>
              <div className="p-4 font-medium text-sm text-center border-l border-white/20">
                <span className="flex items-center justify-center gap-2">
                  <Heart className="w-4 h-4" /> Luna
                </span>
              </div>
            </div>

            {/* Table Body */}
            {comparisonSpecs.map((spec, index) => (
              <div 
                key={index} 
                className={`grid grid-cols-4 border-b border-stone-100 min-w-[600px] ${
                  spec.highlight ? 'bg-gradient-to-r from-cyan-50/50 via-blue-50/50 to-purple-50/50' : 
                  index % 2 === 0 ? 'bg-white' : 'bg-stone-50/50'
                }`}
              >
                <div className="p-4 text-sm font-medium text-muted-foreground flex items-center">
                  {spec.highlight && <Sparkles className="w-3 h-3 text-accent mr-2" />}
                  {spec.label}
                </div>
                <div className="p-4 text-sm text-center border-l border-stone-100">
                  {spec.l1 === "✓" ? (
                    <Check className="w-5 h-5 text-green-500 mx-auto" />
                  ) : spec.l1 === "—" ? (
                    <X className="w-5 h-5 text-stone-300 mx-auto" />
                  ) : (
                    <span className="text-primary font-medium text-xs">{spec.l1}</span>
                  )}
                </div>
                <div className="p-4 text-sm text-center border-l border-stone-100">
                  {spec.m3 === "✓" ? (
                    <Check className="w-5 h-5 text-green-500 mx-auto" />
                  ) : spec.m3 === "—" ? (
                    <X className="w-5 h-5 text-stone-300 mx-auto" />
                  ) : (
                    <span className="text-primary font-medium text-xs">{spec.m3}</span>
                  )}
                </div>
                <div className="p-4 text-sm text-center border-l border-stone-100">
                  {spec.luna === "✓" ? (
                    <Check className="w-5 h-5 text-green-500 mx-auto" />
                  ) : spec.luna === "—" ? (
                    <X className="w-5 h-5 text-stone-300 mx-auto" />
                  ) : (
                    <span className="text-primary font-medium text-xs">{spec.luna}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Best For Section */}
      <section className="py-16 px-6 lg:px-12 fade-in-observer">
        <div className="container mx-auto max-w-7xl">
          <h2 className="font-playfair text-3xl font-medium text-primary mb-12 text-center">Which One is Right for You?</h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            {/* L1 Best For */}
            <div className="bg-gradient-to-br from-cyan-50 to-cyan-100/50 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-6">
                <Cpu className="w-8 h-8 text-cyan-600" />
                <h3 className="font-playfair text-xl font-medium text-primary">Choose L1 If:</h3>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-cyan-600 mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground text-sm">You want maximum features in compact size</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-cyan-600 mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground text-sm">UV sterilization is a priority</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-cyan-600 mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground text-sm">You need foam shield splash prevention</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-cyan-600 mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground text-sm">Space efficiency is important</span>
                </li>
              </ul>
              <Button asChild className="w-full mt-6 bg-cyan-600 hover:bg-cyan-700">
                <LocalizedLink to="/products/l1">Explore ZENO L1</LocalizedLink>
              </Button>
            </div>

            {/* M3 Best For */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-6">
                <Zap className="w-8 h-8 text-blue-600" />
                <h3 className="font-playfair text-xl font-medium text-primary">Choose M3 If:</h3>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground text-sm">You want voice control for hands-free use</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground text-sm">Radar sensor auto detection is preferred</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground text-sm">You want foot kick seat ring control</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground text-sm">Tech-forward automation matters</span>
                </li>
              </ul>
              <Button asChild className="w-full mt-6 bg-blue-600 hover:bg-blue-700">
                <LocalizedLink to="/products/m3">Explore M3 Smart</LocalizedLink>
              </Button>
            </div>

            {/* Luna Best For */}
            <div className="bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-6">
                <Heart className="w-8 h-8 text-purple-600" />
                <h3 className="font-playfair text-xl font-medium text-primary">Choose Luna If:</h3>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground text-sm">Wellness and health features are priority</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground text-sm">You want aromatherapy integration</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground text-sm">Health monitoring sensors appeal to you</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground text-sm">You prefer 5-year comprehensive warranty</span>
                </li>
              </ul>
              <Button asChild className="w-full mt-6 bg-purple-600 hover:bg-purple-700">
                <LocalizedLink to="/products/luna">Explore Luna</LocalizedLink>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 lg:px-12 fade-in-observer">
        <div className="container mx-auto max-w-4xl">
          <ProminentCTA
            title="Need Help Choosing?"
            description="Our B2B specialists can help you select the perfect smart toilet models for your project requirements."
            primaryCTA={{ text: "Request Consultation", href: "/contact" }}
            secondaryCTA={{ text: "View All Models", href: "/smart-toilets" }}
          />
        </div>
      </section>

      <Footer />

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 z-50 p-3 bg-primary text-primary-foreground rounded-full shadow-lg hover:bg-primary/90 transition-all duration-300"
          aria-label="Back to top"
        >
          <ArrowUp className="w-5 h-5" />
        </button>
      )}
    </div>
  );
}
